'use client';

import { useShallow } from 'zustand/react/shallow';
import { useParams } from 'next/navigation';
import { useConceptStore } from '../model/concept-editor-store';
import { useConcept } from './use-concepts';

/**
 * Hook for reading concept header data — project-level fields come from
 * the store (set by project pages), concept-level fields are derived from
 * the canonical TanStack `useConcept(projectId, conceptId)` query keyed
 * off the URL params. No mirror, no setConcept — header always reflects
 * the cached concept for the current route.
 */
export function useConceptHeaderData() {
  const projectData = useConceptStore(
    useShallow((state) => ({
      projectId: state.mobileHeader.projectId,
      projectName: state.mobileHeader.projectName,
    }))
  );

  const params = useParams();
  const projectIdParam =
    typeof params.projectId === 'string' ? params.projectId : undefined;
  const conceptIdParam =
    typeof params.conceptId === 'string' ? params.conceptId : undefined;

  const { data: concept } = useConcept(projectIdParam, conceptIdParam);

  // Optional chain on `attributes` too: JSON:API responses for concept can
  // arrive with `data: { id, type }` only (sparse fieldsets, prefetch
  // placeholders, mid-fetch transitions). Non-optional access here crashes
  // the whole `(auth)` layout — see `app/(auth)/layout.tsx:56`.
  return {
    projectId: projectData.projectId,
    projectName: projectData.projectName,
    conceptId: concept?.id ?? null,
    conceptSequenceNumber:
      concept?.attributes?.project_sequence_number ?? null,
    conceptTitle: concept?.attributes?.title ?? null,
    isFavorite: concept?.attributes?.is_favorite ?? false,
    prevConceptId: concept?.meta?.navigation?.prev_concept_id ?? null,
    nextConceptId: concept?.meta?.navigation?.next_concept_id ?? null,
  };
}

/**
 * Hook for updating mobile-header *project-level* data (project name).
 * Concept-level fields are no longer writable here — they come from
 * `useConcept()` keyed off the URL.
 */
export function useSetConceptHeaderData() {
  const setMobileHeaderData = useConceptStore(
    (state) => state.setMobileHeaderData
  );
  return setMobileHeaderData;
}
